export { env, supabaseConfigured } from './env';
export type { ArModuleSource, TransitionMode } from './env';
export { resolveMode, adminRequested } from './mode';
export type { AppMode } from './mode';
export { buildConfig } from './flags';
export type { AppConfig } from './flags';

/**
 * Timings for the transition and capture window. Provisional — the transition
 * numbers are pinned to the MP4 spec that is still open with Super Objective.
 * See docs/OPEN_ITEMS.md.
 */
export const timing = {
  /**
   * Beat between the trigger firing and the transition starting.
   *
   * Back to zero (2026-09-16). It was one second, and the caveat that had been
   * flagged against it is what happened on the device: on the Camera Kit path
   * the Lens fires its own transformation off the open mouth and
   * `setCostumeVisible` is a no-op there, so nothing in our code puts the
   * costume on and nothing in our code can postpone it. A hold does not delay
   * the change, only its cover — so the second bought no caw, it just left the
   * change naked on screen for a second before the fog arrived to hide it.
   *
   * The fog cannot start any earlier than this. The trigger IS the open mouth,
   * which is the same frame the Lens transforms on, so there is nothing to
   * anticipate — zero is the floor, not a tuning choice.
   *
   * This only becomes a real trade-off again once the AR module honours
   * setCostumeVisible — the PixelChefs module is contracted to
   * (src/ar/types.ts) and the stub already does. On that path the costume waits
   * for `occlusionAtMs` and a hold costs nothing, so the client's original 3-5s
   * ask (2026-09-14) can come back with it. Until then it cannot.
   */
  cawHoldMs: 0,
  // The trigger grace lives in AppConfig (src/config/flags.ts), not here: it
  // is 3s on the kiosk and 0 on web, and the reason is worth reading there.
  /**
   * Picture kept from BEFORE the trigger, so the clip opens on the guest as
   * they were rather than already mid-caw.
   *
   * This is a cycle length, not an exact lead-in: PreRollRecorder keeps
   * whichever of its two recorders has been running longest, so what actually
   * lands in the clip is between half this and all of it — roughly 0.6s to
   * 1.2s. Tightening the window means more concurrent encoders during the live
   * phase, which the kiosk board pays for. See src/capture/preRoll.ts.
   */
  preRollMs: 1200,
  /**
   * Length of the transition MP4. Measured off the delivered fog master, not
   * nominal: the trimmed clip is 3.28s (see scripts/build-art.mjs).
   */
  transitionMs: 3280,
  /**
   * Point in the transition with the heaviest coverage, where we flip the
   * costume on. Measured off the alpha plane of the SHIPPED clip rather than
   * assumed: peak mean opacity is at 1.95s in the master, which is 0.58s into
   * the trimmed clip.
   *
   * Was 1340, off the master's own framing, which put the flip 340ms PAST peak
   * and onto the decay side — thinner cover than the file can give. The repack
   * centre-crops to 9:16, so the master and the shipped file peak at different
   * times; the shipped one is the one on screen.
   *
   * Note the fog never fully occludes — peak mean coverage is about 78%, so
   * this hides a costume swap well but not completely. It is the right moment,
   * not an opaque one. See docs/OPEN_ITEMS.md.
   */
  occlusionAtMs: 580,
  /**
   * How long the reveal is held before the clip is presented.
   *
   * Three seconds (2026-09-20). It had been cut to one on 2026-09-15 when the
   * clip ran long, and with the fog now landing at its peak the mask got barely
   * a second on screen and in the clip — "the video ends too shortly after the
   * transition". Two seconds back buys two more seconds of the costume, which
   * is the payoff, at a clip of roughly 6.5-8.5s.
   *
   * Note this number does double duty: it is the tail of the recording AND the
   * dwell on the reveal before the flow moves to the result screen, because the
   * recording stops when the hold ends. Changing it changes both. That is the
   * right coupling — a clip that ends before the guest stops looking at the
   * same frame would be odd — and the result screen then comes straight up
   * with the clip already looping.
   *
   * The fog has decayed below 7% by the end of `transitionMs`, so the costume
   * is already clear when this hold starts. It is not carrying the reveal on
   * its own.
   */
  /*
   * Zero since 2026-09-21, for the MediaPipe costume.
   *
   * Everything above is the argument for three seconds and it still holds for
   * PixelChefs' lens — the costume is the payoff and it wants looking at. The
   * MediaPipe stand-in is not that costume: it hangs off the head rather than
   * tracking a body, and the longer it is on screen the more obviously. Asked
   * for two seconds post-transformation instead of five.
   *
   * Two seconds is not reachable from here. The costume lands 580ms into a
   * 3280ms transition, so 2700ms of fog-clearing follows it before this hold
   * even starts — that is the floor, and going under it means a shorter fog and
   * a re-measured `occlusionAtMs`.
   *
   * It also turns out to be the slack the fog finishes in. Cut to 0 on
   * 2026-09-21 to shorten the tail for the MediaPipe stand-in costume, and the
   * fog stopped playing entirely: the clip then ends exactly as the fog ends,
   * the flow goes straight to the result screen, the panel reconnects ahead
   * from there, and the stage's `freshPc` calls `stopFog()` on a fog still
   * settling — `play()` interrupted by `pause()`, no fog at all. Anything under
   * a few hundred ms here is not safe.
   *
   * **This is deliberately NOT mode-aware**, and it is shared with the web
   * flow. The kiosk ran 600 for one evening (a ~3.3s tail) because the MediaPipe
   * costume does not reward a long look; that is a property of that stand-in,
   * not of the activation, and PixelChefs' lens is the one that ships. If the
   * kiosk needs its own shorter tail permanently, this belongs in AppConfig
   * (src/config/flags.ts) next to `triggerGraceMs` rather than being cut here
   * and quietly shortening the web reveal too. Only CaptureScreen reads it.
   */
  revealHoldMs: 3000,
  /**
   * Intended clip length: the pre-roll, the caw hold, the whole transition, and
   * the reveal hold. Recording spans the trigger rather than starting at
   * occlusion, so the transformation is in the clip — which is the payoff. See
   * CaptureScreen's startRecording.
   *
   * Derived, so it cannot drift from the four numbers above. Drives the
   * on-screen duration bar; the recorder is still stopped explicitly. The holds
   * have to be in here or the bar fills early and then sits at 100%.
   *
   * Nominal at the top end, because `preRollMs` is a window: a clip usually
   * runs a few hundred ms shorter than this. Only the dev duration readout
   * reads it, so a bar that fills a touch late is the right way round.
   */
  get clipTargetMs(): number {
    return this.preRollMs + this.cawHoldMs + this.transitionMs + this.revealHoldMs;
  },
  /**
   * Hard cap on the recording, not the intended length — roughly twice
   * clipTargetMs. Only exists so a stuck flow cannot record forever.
   */
  recordMaxMs: 18_000,
  /**
   * Kiosk end card dwell before auto-reset to attract. Storyboard asks for
   * "auto-reset to attract after 3-4 seconds".
   */
  endCardMs: 3500,

  /**
   * Trigger fallbacks. If the guest is on the live camera this long without
   * onTrigger firing, something is not working for them — bad light, a mask, a
   * beard, or simply detection that does not like their face.
   *
   * First a nudge, then a way through. Getting someone stranded in front of a
   * camera is worse than letting them transform without a perfect caw, and
   * until we can judge the vendor's detection quality this is the only thing
   * standing between a bad detector and a dead end.
   */
  triggerHintAfterMs: 18_000,
  triggerFallbackAfterMs: 35_000,

  /**
   * How long the camera may take to come up before we say anything about it.
   *
   * Both flows now open the camera one screen early — the web permission step
   * and, since 2026-09-16, the kiosk's instruction clip — so by the time this
   * screen mounts the device is usually already running and the only work left
   * is the Lens session and a first frame. Announcing "Opening the camera…" for
   * the couple of hundred milliseconds that takes is worse than saying nothing:
   * it reads as a loading screen for something that was not actually loading.
   *
   * That held when the kiosk's cover was a 19.7s clip. The 2026-09-17 recut is
   * 3.1s, so on the panel the open now has about three seconds of head start
   * rather than twenty, and this threshold is the thing that will show it: if
   * the card starts appearing on the unit, the open is genuinely outrunning the
   * clip and the fix is a longer clip or an earlier open, not a larger number
   * here. Unmeasured on the real hardware — left at 700ms deliberately.
   *
   * So the line waits. A genuinely slow open — a cold device, a camera another
   * app still holds — crosses this and still explains itself rather than
   * leaving a guest in front of a black panel.
   */
  openingNoticeAfterMs: 700,
} as const;
